// ── Feed Store ─────────────────────────────────────────────────────────────────
// localStorage-backed data layer for My Feed feature.
// Designed so each function can be swapped for real API calls when the backend
// endpoints are available.

export type InsuranceType = 'cyber' | 'professional-indemnity' | 'public-liability' | 'general';

export interface FeedBanner {
  id: string;
  title: string;
  message: string;
  type: InsuranceType;
  ctaLabel: string;
  isActive: boolean;
  targetAll: boolean;
  targetClientIds: number[];
  createdAt: string;
}

export interface Newsletter {
  id: string;
  title: string;
  excerpt: string;
  body: string;
  category: string;
  readTime: number; // minutes
  isPublished: boolean;
  targetAll: boolean;
  targetClientIds: number[];
  publishedAt: string;
  headerImage?: string; // base64 encoded, compressed at upload time
}

export interface FeedDocument {
  id: string;
  name: string;
  description: string;
  category: 'policy' | 'claim-form' | 'guide' | 'other';
  filename: string;
  fileData: string; // base64, empty for seeded placeholders
  fileSize: number; // bytes
  url?: string;     // external link — when set, clicking opens the URL instead of the PDF viewer
  targetAll: boolean;
  targetClientIds: number[];
  uploadedAt: string;
}

export interface RenewalDate {
  userId: number;
  type: InsuranceType;
  renewalDate: string;
  setAt: string;
}

// ── Insurance type display config ─────────────────────────────────────────────
export const INSURANCE_TYPE_CONFIG: Record<InsuranceType, {
  label: string; color: string; icon: string; bgGradient: string;
}> = {
  cyber: {
    label: 'Cyber Insurance',
    color: '#4f46e5',
    icon: '🔒',
    bgGradient: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
  },
  'professional-indemnity': {
    label: 'Professional Indemnity',
    color: '#0284c7',
    icon: '🛡️',
    bgGradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
  },
  'public-liability': {
    label: 'Public Liability',
    color: '#059669',
    icon: '⚡',
    bgGradient: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
  },
  general: {
    label: 'General Update',
    color: '#001f3f',
    icon: '📋',
    bgGradient: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
  },
};

// ── Storage keys ──────────────────────────────────────────────────────────────
const BANNERS_KEY   = 'ys_feed_banners_v1';
const NL_KEY        = 'ys_feed_newsletters_v1';
const DOCS_KEY      = 'ys_feed_documents_v2'; // bumped to v2: claims form now has url field
const RENEWALS_KEY  = 'ys_feed_renewals_v1';
const SEEDED_KEY    = 'ys_feed_seeded_v3';   // bumped to v3: updated Motor Claims description wording

function load<T>(key: string): T[] {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : []; }
  catch { return []; }
}
function persist<T>(key: string, data: T[]): void {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* quota */ }
}

// ── Seed data ─────────────────────────────────────────────────────────────────
const SEED_BANNERS: FeedBanner[] = [
  {
    id: 'seed-b-1',
    title: 'Cyber Insurance Renewal Reminder',
    message: 'Your cyber insurance policy may be coming up for renewal. Log your renewal date now to make sure your business stays fully protected against digital threats.',
    type: 'cyber',
    ctaLabel: 'Log Renewal Date',
    isActive: true,
    targetAll: true,
    targetClientIds: [],
    createdAt: new Date().toISOString(),
  },
  {
    id: 'seed-b-2',
    title: 'Professional Indemnity Cover Review',
    message: 'As your business evolves, so should your Professional Indemnity cover. Set your renewal date and let your account manager help ensure you\'re fully protected.',
    type: 'professional-indemnity',
    ctaLabel: 'Set Renewal Date',
    isActive: true,
    targetAll: true,
    targetClientIds: [],
    createdAt: new Date().toISOString(),
  },
  {
    id: 'seed-b-3',
    title: 'Public Liability Update',
    message: 'New regulatory guidance for fleet operators on public liability requirements has been issued. Review what\'s changed and update your policy records accordingly.',
    type: 'public-liability',
    ctaLabel: 'View Update',
    isActive: true,
    targetAll: true,
    targetClientIds: [],
    createdAt: new Date().toISOString(),
  },
];

const SEED_NEWSLETTERS: Newsletter[] = [
  {
    id: 'seed-nl-1',
    title: 'Protecting Your Fleet Against Cyber Threats in 2025',
    excerpt: 'As fleet operations increasingly rely on connected technology — from telematics to fleet management platforms — cyber threats pose a growing risk to businesses of all sizes. Here\'s what you need to know.',
    body: `<h2>The Rising Cyber Risk for Fleet Operators</h2>
<p>Modern fleet management relies heavily on digital systems: online portals, telematics, GPS tracking, and cloud-based management tools. While these technologies bring efficiency and visibility, they also create new attack surfaces for cybercriminals.</p>
<p>In 2024, attacks on logistics and transportation businesses increased by 38%, with ransomware being the most common threat vector. The average cost of a cyber incident for an SME now exceeds £30,000 — and that's before reputational damage is factored in.</p>
<h2>Key Threats to Watch</h2>
<ul>
  <li><strong>Phishing attacks</strong> — Fraudulent emails impersonating suppliers, insurers or DVLA</li>
  <li><strong>Ransomware</strong> — Malicious software that encrypts your systems and demands payment</li>
  <li><strong>Data breaches</strong> — Unauthorised access to driver, vehicle and client data</li>
  <li><strong>Telematics vulnerabilities</strong> — Exploitation of poorly secured connected devices</li>
</ul>
<h2>How Cyber Insurance Helps</h2>
<p>A comprehensive cyber insurance policy provides financial protection and expert incident response support. Coverage typically includes incident response costs, business interruption losses, data recovery expenses, and regulatory fines.</p>
<p>Yourshield works with leading cyber insurers to ensure our clients have the right level of protection. If you haven't reviewed your cyber coverage recently, now is the ideal time to act.</p>
<p><strong>Contact your account manager today to discuss your cyber insurance needs.</strong></p>`,
    category: 'Cyber Security',
    readTime: 5,
    isPublished: true,
    targetAll: true,
    targetClientIds: [],
    publishedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'seed-nl-2',
    title: '5 Ways to Reduce Your Fleet Insurance Premiums',
    excerpt: 'Rising costs are putting pressure on fleet operators everywhere. Here are five proven strategies to reduce your insurance premiums without compromising on the cover your business needs.',
    body: `<h2>Managing Fleet Insurance Costs</h2>
<p>Insurance premiums for commercial fleets have risen significantly in recent years. But there are concrete steps you can take to manage your premium spend proactively.</p>
<h2>1. Invest in Driver Training</h2>
<p>Insurers reward clients who demonstrate a commitment to driver safety. IATP-accredited driver training programmes, regular licence checks, and first-aid training all signal reduced risk — and should be reflected in your premium at renewal.</p>
<h2>2. Implement a Named Driver Policy</h2>
<p>Any-driver policies carry a higher risk premium. Moving to a named driver policy through Myshield demonstrates controlled risk and can reduce your costs significantly.</p>
<h2>3. Use Telematics Data</h2>
<p>Usage-based insurance (UBI) uses real driving data to price risk more accurately. If your fleet maintains a good safety record, telematics data is your best tool at renewal.</p>
<h2>4. Improve Vehicle Security</h2>
<p>Fitted trackers, immobilisers, and secure overnight parking all reduce theft risk. Ensure your insurer is fully aware of every security measure in place.</p>
<h2>5. Review Your Cover Annually</h2>
<p>Business needs change. Vehicles added to a policy long after purchase, incorrect mileage estimates, or outdated business descriptions can all lead to overpaying. An annual review with Yourshield ensures your cover is accurate and optimally priced.</p>`,
    category: 'Cost Management',
    readTime: 4,
    isPublished: true,
    targetAll: true,
    targetClientIds: [],
    publishedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'seed-nl-3',
    title: 'Professional Indemnity Insurance: A Fleet Operator\'s Guide',
    excerpt: 'Professional Indemnity insurance is often overlooked by fleet businesses, yet it provides essential protection against costly claims. We explain what it covers, who needs it, and why it matters.',
    body: `<h2>What is Professional Indemnity Insurance?</h2>
<p>Professional Indemnity (PI) insurance protects your business against claims made by clients for financial loss resulting from your professional advice or services. For fleet management companies, consultants, and operators providing services to others, PI cover is essential.</p>
<h2>Who Needs PI Cover?</h2>
<p>If your business provides advice, consultancy, or management services to other businesses — including fleet management services — you should consider PI insurance. Claims can arise from errors in advice, contract disputes, or unintended negligence.</p>
<h2>Typical Claims Scenarios</h2>
<ul>
  <li>A fleet management company advises a client on a risk management strategy that subsequently fails</li>
  <li>An error in vehicle compliance reporting leads to a regulatory penalty for a client</li>
  <li>Incorrect maintenance records provided to a third party result in financial loss</li>
</ul>
<h2>How Yourshield Can Help</h2>
<p>We work with specialist PI insurers to arrange cover tailored to your specific activities. Cover limits, retroactive dates, and policy scope all need careful consideration — which is why we recommend an annual review with your account manager.</p>`,
    category: 'Professional Advice',
    readTime: 4,
    isPublished: true,
    targetAll: true,
    targetClientIds: [],
    publishedAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'seed-nl-4',
    title: 'Named Driver Policy Changes: What to Know in 2025',
    excerpt: 'Regulatory updates to named driver policies are in effect this year. Here\'s what fleet operators need to know to stay compliant and avoid complications at the point of claim.',
    body: `<h2>2025 Named Driver Policy Updates</h2>
<p>The insurance market has introduced new requirements for named driver policies in commercial fleets. These changes are designed to improve transparency and reduce fraud — but they require action from fleet operators.</p>
<h2>Key Changes</h2>
<ul>
  <li><strong>DVLA licence checks</strong> — More frequent mandatory checks for all named drivers on fleet policies</li>
  <li><strong>Enhanced declarations</strong> — Drivers must declare all convictions, including minor offences, at each renewal</li>
  <li><strong>Digital verification</strong> — Insurers increasingly require digital evidence of completed licence checks</li>
</ul>
<h2>How Myshield Helps</h2>
<p>The Myshield portal is designed to make compliance straightforward. Our named driver management tools allow you to maintain accurate, up-to-date records for all drivers on your policy, with built-in licence expiry alerts.</p>
<p>If you have any questions about how these changes affect your current policy, please contact your Yourshield account manager directly.</p>`,
    category: 'Policy Updates',
    readTime: 3,
    isPublished: true,
    targetAll: true,
    targetClientIds: [],
    publishedAt: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const SEED_DOCUMENTS: FeedDocument[] = [
  {
    id: 'seed-doc-1',
    name: 'Fleet Insurance Policy Schedule',
    description: 'Your comprehensive fleet insurance policy schedule for the current policy period, including all covered vehicles and named drivers.',
    category: 'policy',
    filename: 'fleet-policy-schedule.pdf',
    fileData: '',
    fileSize: 0,
    targetAll: true,
    targetClientIds: [],
    uploadedAt: new Date().toISOString(),
  },
  {
    id: 'seed-doc-2',
    name: 'Motor Claims',
    description: 'Navigate to our Motor Claims Form that you can complete and save before sending to us.',
    category: 'claim-form',
    filename: '',
    fileData: '',
    fileSize: 0,
    url: 'https://www.yourshield.co.uk/wp-content/uploads/2025/10/motor-claims-form-11032024.pdf',
    targetAll: true,
    targetClientIds: [],
    uploadedAt: new Date().toISOString(),
  },
  {
    id: 'seed-doc-3',
    name: 'Named Driver Addition Form',
    description: 'Use this form to add a new named driver to your fleet policy. Valid driving licence details are required for all additions.',
    category: 'claim-form',
    filename: 'named-driver-addition.pdf',
    fileData: '',
    fileSize: 0,
    targetAll: true,
    targetClientIds: [],
    uploadedAt: new Date().toISOString(),
  },
];

function seedIfNeeded(): void {
  if (localStorage.getItem(SEEDED_KEY)) return;
  persist(BANNERS_KEY, SEED_BANNERS);
  persist(NL_KEY, SEED_NEWSLETTERS);
  persist(DOCS_KEY, SEED_DOCUMENTS);
  localStorage.setItem(SEEDED_KEY, '1');
}

// ── Banners store ─────────────────────────────────────────────────────────────
export const feedBannersStore = {
  init: seedIfNeeded,

  getAll(): FeedBanner[] {
    seedIfNeeded();
    return load<FeedBanner>(BANNERS_KEY);
  },

  /** Returns active banners visible to a given client (or all active if no clientId). */
  forClient(clientId?: number): FeedBanner[] {
    return this.getAll().filter(b =>
      b.isActive && (b.targetAll || (clientId != null && b.targetClientIds.includes(clientId)))
    );
  },

  add(item: Omit<FeedBanner, 'id' | 'createdAt'>): FeedBanner {
    const newItem: FeedBanner = { ...item, id: `b-${Date.now()}`, createdAt: new Date().toISOString() };
    persist(BANNERS_KEY, [...this.getAll(), newItem]);
    return newItem;
  },

  update(id: string, patch: Partial<FeedBanner>): void {
    persist(BANNERS_KEY, this.getAll().map(b => b.id === id ? { ...b, ...patch } : b));
  },

  delete(id: string): void {
    persist(BANNERS_KEY, this.getAll().filter(b => b.id !== id));
  },
};

// ── Newsletters store ─────────────────────────────────────────────────────────
export const feedNewslettersStore = {
  getAll(): Newsletter[] {
    seedIfNeeded();
    return load<Newsletter>(NL_KEY);
  },

  forClient(clientId?: number): Newsletter[] {
    return this.getAll().filter(n =>
      n.isPublished && (n.targetAll || (clientId != null && n.targetClientIds.includes(clientId)))
    );
  },

  add(item: Omit<Newsletter, 'id' | 'publishedAt'>): Newsletter {
    const newItem: Newsletter = { ...item, id: `nl-${Date.now()}`, publishedAt: new Date().toISOString() };
    persist(NL_KEY, [...this.getAll(), newItem]);
    return newItem;
  },

  update(id: string, patch: Partial<Newsletter>): void {
    persist(NL_KEY, this.getAll().map(n => {
      if (n.id !== id) return n;
      const next = { ...n, ...patch };
      // Mirror API behaviour: refreshing content bumps the visible date.
      if (next.isPublished !== false) {
        next.publishedAt = new Date().toISOString();
      }
      return next;
    }));
  },

  delete(id: string): void {
    persist(NL_KEY, this.getAll().filter(n => n.id !== id));
  },
};

// ── Documents store ───────────────────────────────────────────────────────────
export const feedDocumentsStore = {
  getAll(): FeedDocument[] {
    seedIfNeeded();
    return load<FeedDocument>(DOCS_KEY);
  },

  forClient(clientId?: number): FeedDocument[] {
    return this.getAll().filter(d =>
      (!!d.fileData || !!d.url) && // show if has an uploaded file OR an external link
      (d.targetAll || (clientId != null && d.targetClientIds.includes(clientId)))
    );
  },

  add(item: Omit<FeedDocument, 'id' | 'uploadedAt'>): FeedDocument {
    const newItem: FeedDocument = { ...item, id: `doc-${Date.now()}`, uploadedAt: new Date().toISOString() };
    persist(DOCS_KEY, [...this.getAll(), newItem]);
    return newItem;
  },

  delete(id: string): void {
    persist(DOCS_KEY, this.getAll().filter(d => d.id !== id));
  },
};

// ── Renewals store ────────────────────────────────────────────────────────────
export const feedRenewalsStore = {
  get(userId: number): RenewalDate[] {
    return load<RenewalDate>(RENEWALS_KEY).filter(r => r.userId === userId);
  },

  getOne(userId: number, type: InsuranceType): RenewalDate | undefined {
    return load<RenewalDate>(RENEWALS_KEY).find(r => r.userId === userId && r.type === type);
  },

  set(userId: number, type: InsuranceType, renewalDate: string): void {
    const all = load<RenewalDate>(RENEWALS_KEY).filter(r => !(r.userId === userId && r.type === type));
    persist(RENEWALS_KEY, [...all, { userId, type, renewalDate, setAt: new Date().toISOString() }]);
  },
};
